import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { FadeIn } from "@/components/site/FadeIn";
import { POEMS, ABOUT } from "@/data/book";

export const SamplePoems = () => {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const poem = POEMS[active];

  const copyVerse = async () => {
    const text = `${poem.title}\n\n${poem.lines.join("\n")}\n\n— Isaac Xavier, Ascent of the Hummingbird`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Verse copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy verse");
    }
  };

  return (
    <section
      id="poems"
      data-testid="sample-poems-section"
      className="relative py-28 sm:py-36 bg-[#201c19] text-[#f5f2ea] overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-[#2a7b76]/15 blur-[140px]" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6">
        <FadeIn className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.28em] text-[#8fc0bb] font-semibold mb-5">
            A taste of the voice
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light">
            Sample poems
          </h2>
          <p className="mt-6 mx-auto max-w-2xl font-display text-xl sm:text-2xl italic text-[#cfc9c1] leading-relaxed">
            {ABOUT.readerNote}
          </p>
        </FadeIn>

        <FadeIn delay={0.1} className="flex flex-wrap justify-center gap-3 mb-14" data-testid="poem-selector-tabs">
          {POEMS.map((p, i) => (
            <button
              key={p.title}
              onClick={() => setActive(i)}
              data-testid={`poem-tab-${i}`}
              className={`rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 ${
                i === active
                  ? "bg-[#f5f2ea] text-[#201c19]"
                  : "border border-white/20 text-[#cfc9c1] hover:border-white/50"
              }`}
            >
              {p.title}
            </button>
          ))}
        </FadeIn>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            data-testid="poem-card-active"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
          >
            <span
              data-testid="poem-theme-badge"
              className="inline-block mb-8 rounded-full border border-[#2a7b76]/60 text-[#8fc0bb] text-xs uppercase tracking-[0.2em] px-4 py-1.5"
            >
              {poem.theme}
            </span>

            <h3 data-testid="poem-title" className="font-display text-4xl sm:text-5xl italic mb-10">
              {poem.title}
            </h3>

            <div data-testid="poem-body" className="space-y-3">
              {poem.lines.map((line, idx) =>
                line === "" ? (
                  <div key={`${poem.title}-${idx}`} className="h-5" />
                ) : (
                  <p
                    key={`${poem.title}-${idx}`}
                    className="poem-line font-display text-2xl sm:text-3xl font-light leading-relaxed text-[#f5f2ea]/95"
                    style={{ animationDelay: `${idx * 45}ms` }}
                  >
                    {line}
                  </p>
                )
              )}
            </div>

            <button
              onClick={copyVerse}
              data-testid="poem-copy-verse-btn"
              className="mt-12 inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-medium text-[#cfc9c1] hover:border-white/60 hover:text-white transition-colors duration-300"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copied" : "Copy verse"}
            </button>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SamplePoems;
